// @ts-nocheck
/*
 * Voice-profile list.
 *
 * Default (seeded) profiles are super-admin territory: no other role, and
 * no ACL, may touch them. Everything else is a call-centre admin's own
 * profile, which they may create / edit / delete only with the matching
 * TTS ACL operation.
 */
import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import type { ColumnDef } from '@tanstack/react-table';
import { getApiCall } from '@/service/getApiCall';
import { deleteApiCallWithHeader } from '@/service/deleteApiCall';
import { API_VOICE_PROFILE, API_VOICE_PROFILE_BY_ID } from '@/service/url';
import { ROUTES, USER_ROLES, MODULE_ACL } from '@/utilities/constant';
import { useModuleAcl } from '@/hooks/useModuleAcl';
import { Storage } from '@/utilities/setting';
import { toast } from 'sonner';
import { friendlyApiError } from '@/utilities/apiErrorMessage';
import {
  Plus,
  Pencil,
  Trash2,
  ShieldCheck,
  RefreshCw,
  Gauge,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { DataTable } from '@/components/data-table/DataTable';
import { FillPage } from '@/components/layout/PageContainer';
import { PageHeader } from '@/components/feedback/PageHeader';
import Loader from '@/components/partials/components/Loader';
import { ConfirmDialog } from '@/components/feedback/ConfirmDialog';

const initialsOf = (name?: string) => {
  if (!name) return '?';
  const parts = String(name).trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

const creatorOf = (p: any) =>
  p?.profile_creator?.name ||
  p?.profile_creator?.username ||
  (p?.is_default ? 'System' : '—');

const VoiceProfileList = () => {
  const navigate = useNavigate();
  const [profiles, setProfiles] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [deleteTarget, setDeleteTarget] = useState<any>(null);
  const [deleting, setDeleting] = useState(false);

  const role = useMemo(() => {
    try {
      return JSON.parse(Storage.getItem('user') || 'null')?.role || null;
    } catch {
      return null;
    }
  }, []);
  const isSuperAdmin = role === USER_ROLES.SUPERADMIN;
  const isCallCentreAdmin = role === USER_ROLES.CALL_CENTRE_ADMIN;

  const { canAdd: ttsAdd, canEdit: ttsEdit, canDelete: ttsDelete } = useModuleAcl(
    MODULE_ACL.TTS,
  );
  const canCreate = isSuperAdmin || (isCallCentreAdmin && ttsAdd);
  const canEditRow = (p: any) =>
    p?.is_default ? isSuperAdmin : isSuperAdmin || (isCallCentreAdmin && ttsEdit);
  const canDeleteRow = (p: any) =>
    p?.is_default ? isSuperAdmin : isSuperAdmin || (isCallCentreAdmin && ttsDelete);

  const fetchProfiles = () => {
    setLoading(true);
    getApiCall(API_VOICE_PROFILE)
      .then((res: any) => setProfiles(Array.isArray(res) ? res : []))
      .catch((err) => toast.error(friendlyApiError(err)))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchProfiles();
  }, []);

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await deleteApiCallWithHeader(API_VOICE_PROFILE_BY_ID(deleteTarget.id));
      toast.success('Voice profile deleted successfully');
      setProfiles((prev: any[]) => prev.filter((p) => p.id !== deleteTarget.id));
      setDeleteTarget(null);
    } catch (err) {
      toast.error(friendlyApiError(err));
    } finally {
      setDeleting(false);
    }
  };

  const columns: ColumnDef<any>[] = [
    {
      id: 'flow_name',
      header: 'Flow Name',
      accessorFn: (row: any) => row.flow_name || '',
      cell: ({ row }) => {
        const p = row.original;
        return (
          <div className="flex items-center gap-2">
            <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary/10 text-[11px] font-bold text-primary ring-1 ring-inset ring-primary/20">
              {initialsOf(p.flow_name)}
            </span>
            <div className="min-w-0">
              <div className="flex items-center gap-1.5">
                <span className="text-sm font-semibold text-foreground">
                  {p.flow_name || '—'}
                </span>
                {p.is_default && (
                  <span className="inline-flex items-center gap-1 whitespace-nowrap rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-semibold text-amber-700 ring-1 ring-inset ring-amber-200 dark:bg-amber-950/40 dark:text-amber-300">
                    <ShieldCheck className="h-3 w-3" />
                    Default
                  </span>
                )}
              </div>
              {p.language && (
                <div className="text-[11px] text-muted-foreground">{p.language}</div>
              )}
            </div>
          </div>
        );
      },
    },
    {
      id: 'gender',
      header: 'Gender',
      accessorFn: (row: any) => row.gender || '',
      cell: ({ row }) => {
        const g = row.original.gender;
        if (!g) return <span className="text-muted-foreground">—</span>;
        const isFemale = g === 'female';
        return (
          <span
            className={
              'inline-flex items-center whitespace-nowrap rounded-full px-2.5 py-0.5 text-[11px] font-semibold ring-1 ring-inset ' +
              (isFemale
                ? 'bg-pink-100 text-pink-700 ring-pink-200 dark:bg-pink-950/40 dark:text-pink-300'
                : 'bg-blue-100 text-blue-700 ring-blue-200 dark:bg-blue-950/40 dark:text-blue-300')
            }
          >
            {g.charAt(0).toUpperCase() + g.slice(1)}
          </span>
        );
      },
    },
    {
      id: 'category',
      header: 'Category',
      accessorFn: (row: any) => row.category || '',
      cell: ({ row }) => (
        <span className="text-sm text-foreground">{row.original.category || '—'}</span>
      ),
    },
    {
      id: 'language_tone',
      header: 'Tone',
      accessorFn: (row: any) => row.language_tone || '',
      cell: ({ row }) => (
        <span className="text-sm text-muted-foreground">
          {row.original.language_tone || '—'}
        </span>
      ),
    },
    {
      id: 'pitch',
      header: 'Pitch',
      accessorFn: (row: any) => row.pitch || '',
      cell: ({ row }) => (
        <span className="text-sm text-muted-foreground">{row.original.pitch || '—'}</span>
      ),
    },
    {
      id: 'accent',
      header: 'Accent',
      accessorFn: (row: any) => row.accent || '',
      cell: ({ row }) => (
        <span className="text-sm text-muted-foreground">{row.original.accent || '—'}</span>
      ),
    },
    {
      id: 'speed',
      header: 'Speed',
      accessorFn: (row: any) => Number(row.speed) || 0,
      cell: ({ row }) => (
        <span className="inline-flex items-center gap-1 rounded-md bg-emerald-100 px-1.5 py-0.5 font-mono text-[11px] font-semibold text-emerald-700 ring-1 ring-inset ring-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-300">
          <Gauge className="h-3 w-3" />
          {Number(row.original.speed || 0).toFixed(2)}x
        </span>
      ),
    },
    {
      id: 'created_by',
      header: 'Created By',
      accessorFn: (row: any) => creatorOf(row),
      cell: ({ row }) => (
        <span className="text-sm text-muted-foreground">{creatorOf(row.original)}</span>
      ),
    },
    {
      id: 'created_at',
      header: 'Created At',
      accessorFn: (row: any) => (row.created_at ? new Date(row.created_at).getTime() : 0),
      cell: ({ row }) => (
        <span className="whitespace-nowrap text-xs text-muted-foreground tabular-nums">
          {row.original.created_at
            ? new Date(row.original.created_at).toLocaleDateString('en-IN', {
                day: '2-digit',
                month: 'short',
                year: 'numeric',
              })
            : '—'}
        </span>
      ),
    },
    {
      id: 'actions',
      header: 'Actions',
      enableSorting: false,
      cell: ({ row }) => {
        const p = row.original;
        return (
          <div className="flex items-center gap-1">
            {canEditRow(p) && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  navigate('/' + ROUTES.TTS.VOICE_PROFILE, { state: { profile: p } });
                }}
                className="inline-flex h-8 w-8 items-center justify-center rounded-md bg-primary/10 text-primary ring-1 ring-inset ring-primary/20 transition-colors hover:bg-primary/15"
                title="Edit"
                aria-label="Edit"
              >
                <Pencil className="h-3.5 w-3.5" />
              </button>
            )}
            {canDeleteRow(p) && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setDeleteTarget(p);
                }}
                className="inline-flex h-8 w-8 items-center justify-center rounded-md bg-rose-100 text-rose-700 ring-1 ring-inset ring-rose-200 transition-colors hover:bg-rose-200 dark:bg-rose-950/40 dark:text-rose-300"
                title="Delete"
                aria-label="Delete"
              >
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            )}
            {!canEditRow(p) && !canDeleteRow(p) && (
              <span className="text-xs text-muted-foreground">—</span>
            )}
          </div>
        );
      },
    },
  ];

  if (loading && profiles.length === 0) {
    return (
      <FillPage>
        <Loader />
      </FillPage>
    );
  }

  return (
    <FillPage>
      <PageHeader
        title="Voice Profiles"
        description="All saved voice profiles for your account."
        actions={
          <>
            <Button
              variant="outline"
              size="sm"
              onClick={fetchProfiles}
              disabled={loading}
              className="gap-1.5"
            >
              <RefreshCw className={'h-3.5 w-3.5 ' + (loading ? 'animate-spin' : '')} />
              Refresh
            </Button>
            {canCreate && (
              <Button
                size="sm"
                onClick={() =>
                  navigate('/' + ROUTES.TTS.VOICE_PROFILE, { state: { profile: null } })
                }
                className="gap-1.5"
              >
                <Plus className="h-3.5 w-3.5" />
                Add Profile
              </Button>
            )}
          </>
        }
      />

      <div className="mt-4">
        <DataTable
          tableId="tts.voice-profile-list:v2"
          columns={columns}
          data={profiles}
          emptyTitle="No profiles found"
          emptyDescription='Click "Add Profile" to create one.'
          pageSize={10}
          disableExport
        />
      </div>

      <ConfirmDialog
        open={!!deleteTarget}
        onOpenChange={(next) => !next && !deleting && setDeleteTarget(null)}
        title="Delete voice profile?"
        description={
          deleteTarget ? (
            <>
              Profile <strong>{deleteTarget.flow_name}</strong> (
              {deleteTarget.gender} / {deleteTarget.category}) will be permanently
              removed. This action cannot be undone.
            </>
          ) : (
            ''
          )
        }
        confirmLabel="Delete"
        cancelLabel="Cancel"
        tone="destructive"
        loading={deleting}
        onConfirm={handleDelete}
      />
    </FillPage>
  );
};

export default VoiceProfileList;
